import classNames from "classnames";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { CategoryLabel } from "components/design/CategoryLabel";
import { Divider } from "components/design/Divider";
import { TableUI } from "components/design/TableUI";
import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import Modal from "components/DesignSystem/Modal/Modal";
import { Stepper } from "components/DesignSystem/Stepper/Stepper";
import Table from "components/DesignSystem/Table/V2/Table";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { ArrowRight } from "components/icons/ArrowRight";
import { LockedDatePicker } from "components/LockedDatePicker/LockedDatePicker";
import { NumericInput } from "components/NumericInput/NumericInput";
import { PriceInput } from "components/PriceInput/PriceInput";
import {
  CategoryIndent,
  coaOptions,
} from "components/Transaction/TransactionColumn";
import {
  ACCUMULATED_DEPRECIATION_IDENTIFIER,
  DEPRECIATION_EXPENSE_IDENTIFIER,
  FIXED_ASSET_IDENTIFIER,
} from "constants/categoryIdentifier";
import { DISPLAY_DATE, MMM_YY } from "constants/date";
import dayjs from "dayjs";
import {
  Field,
  FieldProps,
  Form,
  Formik,
  FormikHelpers,
  useFormikContext,
} from "formik";
import { fixedAssetSchema } from "formValidations/fixedAssetSchema";
import { useChartOfAccounts } from "hooks/useChartOfAccounts";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import React, { ReactNode, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import {
  useCreateFixedAssetMutation,
  useSchedulePreviewMutation,
  useUpdateFixedAssetMutation,
} from "store/apis/fixedAsset";
import {
  DepreciationSchedule,
  DepreciationSchedulePreview,
} from "types/Models/fixedAsset";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import { flattenTypes } from "utils/flattenCOA";
import { pluralize } from "utils/pluralize";

type InitialValues = {
  asset_name: string;
  purchase_price: number;
  frequency: string;
  depreciation_start_date: string;
  salvage_amount: number;
  description: string;
  useful_life: number;
  method: string;
  asset_account_id: string;
  depreciation_account_id: string;
  accumulated_depreciation_account_id: string;
  accumulated_depreciation_amount: number;
};

export const RuleStepper = ({
  currentStep,
  onStepperClick,
}: {
  onStepperClick: (step: number) => void;
  currentStep: number;
}) => {
  const breadcrumbs = [
    { name: "Add details", step: 1 },
    { name: "Schedule", step: 2 },
  ];

  return (
    <Stepper size="small" direction="horizontal">
      {breadcrumbs?.map(({ name, step }) => (
        <Stepper.Step
          step={step}
          key={step}
          isActive={currentStep >= step}
          clickable={step === 1}
          onClick={() => onStepperClick(step)}
        >
          <div className="t-flex t-items-center">
            {name}
            {step === 1 && <ArrowRight color="currentColor" />}
          </div>
        </Stepper.Step>
      ))}
    </Stepper>
  );
};

export const ScheduleTable = ({ data }: { data: DepreciationSchedule[] }) => {
  const createColumn = createColumnHelper<DepreciationSchedule>();

  const columns = [
    createColumn.accessor("depreciation_date", {
      size: 40,
      header: "Month",
      cell: (info) => {
        const { depreciation_date } = info.row.original;

        return (
          <div className="t-min-w-12 t-py-1">
            {dayjs(depreciation_date).format(MMM_YY)}
          </div>
        );
      },
    }),

    createColumn.accessor("current_asset_value", {
      size: 20,
      header: () => (
        <div className="t-flex t-justify-end t-whitespace-nowrap">
          Asset value
        </div>
      ),
      cell: (info) => {
        const current_asset_value = info.getValue();

        return (
          <div className="t-flex t-justify-end t-whitespace-nowrap">
            <AmountSuperScript amount={current_asset_value} />
          </div>
        );
      },
    }),

    createColumn.accessor("depreciation_amount", {
      size: 20,
      header: () => (
        <div className="t-flex t-justify-end t-whitespace-nowrap">
          Depreciation
        </div>
      ),
      cell: (info) => {
        const depreciation_amount = info.getValue();

        return (
          <div className="t-flex t-justify-end t-whitespace-nowrap">
            <AmountSuperScript amount={depreciation_amount} />
          </div>
        );
      },
    }),

    createColumn.accessor("remaining_value", {
      size: 20,
      header: () => (
        <div className="t-flex t-justify-end t-whitespace-nowrap">
          Remaining value
        </div>
      ),
      cell: (info) => {
        const remaining_value = info.getValue();

        return (
          <div className="t-flex t-justify-end t-whitespace-nowrap">
            <AmountSuperScript amount={remaining_value} />
          </div>
        );
      },
    }),
  ];

  const table = useReactTable({
    data: data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      size: 10,
      minSize: 1,
      maxSize: 100,
    },
  });

  return (
    <div className="t-overflow-x-auto t-border-neutral-10 t-border-solid t-border t-text-text-60 t-rounded-lg">
      <TableUI
        sizeType="absolute"
        table={table}
        size="small"
        sticky={false}
        header={({ headerGroup, index }) => (
          <Table.Row className="t-bg-surface-lighter-grey">
            {headerGroup.headers.map((header) => {
              return (
                <Table.HeadCell
                  colSpan={header.colSpan}
                  isUpperCase={false}
                  key={header.id}
                  className="!t-text-subtext-sm t-p-3"
                  style={{
                    width: header.getSize(),
                    minWidth: header.column.getSize(),
                  }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </Table.HeadCell>
              );
            })}
          </Table.Row>
        )}
      >
        {({ row }) => {
          return (
            <Table.Row
              key={row.id}
              className="[&:not(:last-child)>td]:!t-bg-white t-py-2"
            >
              {row.getVisibleCells().map((cell) => (
                <Table.Cell
                  className="!t-text-body-sm"
                  key={cell.id}
                  style={{
                    width: cell.column.getSize(),
                    minWidth: cell.column.getSize(),
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Table.Cell>
              ))}
            </Table.Row>
          );
        }}
      </TableUI>
    </div>
  );
};

const Schedule = ({
  close,
  onSubmit,
  currentStep,
  setCurrentStep,
  scheduleData,
  isEditFlow = false,
}: {
  close: () => void;
  onSubmit: (
    { method, ...values }: InitialValues,
    { resetForm }: FormikHelpers<InitialValues>
  ) => void | Promise<any>;
  currentStep: number;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  scheduleData: DepreciationSchedulePreview | undefined;
  isEditFlow?: boolean;
}) => {
  const { fixed_asset, depreciation_schedule = [] } = scheduleData || {};
  const {
    asset_name = "",
    purchase_price = 0,
    description = "",
    useful_life = 0,
    frequency = "",
    depreciation_start_date = "",
    salvage_value = 0,
    asset_account_id = "",
    depreciation_account_id = "",
    accumulated_depreciation_account_id = "",
    accumulated_depreciation_amount = 0,
  } = fixed_asset || {};

  return (
    <Formik
      initialValues={{
        asset_name,
        description,
        purchase_price,
        frequency,
        depreciation_start_date,
        salvage_amount: salvage_value,
        useful_life,
        method: "Straight line",
        asset_account_id,
        depreciation_account_id,
        accumulated_depreciation_account_id,
        accumulated_depreciation_amount,
      }}
      onSubmit={onSubmit}
    >
      {({ submitForm, isSubmitting }) => (
        <Modal.Content size="large" useCustomOverlay>
          <Modal.Header>
            <div>
              <Modal.Title>
                {isEditFlow ? "Edit Fixed Asset" : "Add Fixed Asset"}
              </Modal.Title>
              <Modal.Subtitle>
                <RuleStepper
                  currentStep={currentStep}
                  onStepperClick={setCurrentStep}
                />
              </Modal.Subtitle>
            </div>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body>
            <Form className="t-flex t-flex-col t-gap-6 t-m-0">
              <div className="t-flex t-gap-2 t-flex-col">
                <span className="t-text-subtext">Asset details</span>
                <div className="t-flex t-gap-1 t-text-body-sm t-text-text-30 t-flex-col">
                  <span>Asset name: {asset_name}</span>
                  <span>
                    Useful life: {pluralize(useful_life, "year", "years")}
                  </span>
                </div>
              </div>
              <ScheduleTable data={depreciation_schedule} />
            </Form>
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Button
              onClick={() => setCurrentStep(1)}
              disabled={isSubmitting}
              type="button"
            >
              Back
            </Button>
            <Button
              customType="primary"
              onClick={submitForm}
              isLoading={isSubmitting}
              disabled={isSubmitting}
            >
              {isEditFlow ? "Save" : "Confirm"}
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      )}
    </Formik>
  );
};

export const AssetConsoleItems = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => {
  return (
    <div className="t-flex t-flex-col t-gap-4">
      <span className="t-text-subtext t-text-text-100">{title}</span>
      {children}
    </div>
  );
};

const AssetDetails = () => {
  return (
    <AssetConsoleItems title="Asset details">
      <TextInput
        label="Asset name"
        required
        name="asset_name"
        placeholder="Input asset name"
      />
      <TextInput
        label="Description"
        name="description"
        placeholder="Input description"
      />
    </AssetConsoleItems>
  );
};

const Depreciation = () => {
  const { values } = useFormikContext<InitialValues>();

  const { frequency } = values || {};

  const frequencyOptions = [
    { label: "Monthly", value: "MONTHLY" },
    { label: "Quarterly", value: "QUARTERLY" },
    { label: "Annually", value: "ANNUALLY" },
  ];

  const defaultFrequency = frequencyOptions.find(
    ({ value }) => value === frequency
  );

  return (
    <AssetConsoleItems title="Depreciation">
      <div className="t-grid t-grid-cols-2 t-gap-x-6 t-gap-y-3 sm:t-grid-cols-1">
        <PriceInput
          label="Purchase price"
          required
          name="purchase_price"
          allowNegative={false}
        />
        <Field name="depreciation_start_date">
          {({ field }: FieldProps) => {
            return (
              <LockedDatePicker
                label="Depreciation start date"
                tooltipText="This can be the purchase date."
                {...field}
                portalId="transaction-start"
                block
                required
                maxDate={new Date()}
              />
            );
          }}
        </Field>

        <PriceInput
          label="Salvage value"
          tooltipText="Estimated selling price."
          required
          name="salvage_amount"
          allowNegative={false}
        />
        <TextInput
          label="Depreciation method"
          required
          name="method"
          disabled
        />

        <NumericInput
          label="Useful life (in years)"
          storeNumeric
          fieldProps={{ name: "useful_life" }}
          required
        />
        <Combobox
          withForm
          name="frequency"
          label="Frequency of depreciation"
          placeholder="Select"
          isClearable={false}
          options={frequencyOptions}
          menuPortalTarget={document.body}
          block
          required
          value={
            frequency
              ? {
                  label: defaultFrequency?.label || "",
                  value: defaultFrequency?.value || "",
                }
              : null
          }
        />
      </div>
    </AssetConsoleItems>
  );
};

const Categories = ({
  name,
  label,
}: {
  name:
    | "asset_account_id"
    | "depreciation_account_id"
    | "accumulated_depreciation_account_id";
  label: string;
}) => {
  const { values } = useFormikContext<InitialValues>();
  const { chartOfAccounts = [], isLoading } = useChartOfAccounts({
    hiddenCategoryTypes: ["BANK_ACCOUNT"],
  });

  const category_id = values[name];

  const defaultAssignedCategory = flattenTypes({
    accounts: chartOfAccounts,
  }).find(({ uuid }) => uuid === category_id);

  return (
    <Combobox
      label={label}
      menuPlacement="top"
      isDisabled={isLoading}
      placeholder="Select account"
      menuPortalTarget={document.body}
      withForm
      name={name}
      required
      options={coaOptions(chartOfAccounts)}
      filterOption={(v, i) =>
        v.data.data?.toLocaleLowerCase().includes(i.toLocaleLowerCase()) ||
        false
      }
      value={
        category_id
          ? {
              label: (
                <CategoryIndent
                  label={<CategoryLabel category={defaultAssignedCategory} />}
                  indentLevel={0}
                />
              ),
              value: defaultAssignedCategory?.uuid || "",
            }
          : null
      }
      styles={{
        menuPortal: (base) => ({
          ...base,
          width: 450,
        }),
      }}
    />
  );
};

const Categorization = () => {
  return (
    <AssetConsoleItems title="Assign Categories">
      <div className="t-grid t-grid-cols-2 t-gap-x-6 t-gap-y-3 sm:t-grid-cols-1">
        <Categories label="Asset account" name="asset_account_id" />
        <Categories
          label="Depreciation expense account"
          name="depreciation_account_id"
        />
        <Categories
          label="Accumulated depreciation account"
          name="accumulated_depreciation_account_id"
        />
        <PriceInput
          allowNegative={false}
          label="Accumulated depreciation amount"
          name="accumulated_depreciation_amount"
        />
      </div>
    </AssetConsoleItems>
  );
};

const AssetConsole = ({
  onSubmit,
  close,
  currentStep,
  setCurrentStep,
  scheduleData,
  isEditFlow = false,
}: {
  onSubmit: (
    values: InitialValues,
    formikHelpers: FormikHelpers<InitialValues>
  ) => void | Promise<any>;
  close: () => void;
  currentStep: number;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  scheduleData: DepreciationSchedulePreview | undefined;
  isEditFlow?: boolean;
}) => {
  const { chartOfAccounts = [] } = useChartOfAccounts({
    hiddenCategoryTypes: ["BANK_ACCOUNT"],
  });

  const flattenedAccounts = flattenTypes({ accounts: chartOfAccounts });

  const findCategoryByIdentifier = (identifier: number) =>
    flattenedAccounts.find((account) => account.identifier === identifier)
      ?.uuid || "";

  const { fixed_asset } = scheduleData || {};
  const {
    asset_name = "",
    purchase_price = 0,
    description = "",
    useful_life = 0,
    frequency = "",
    depreciation_start_date = "",
    salvage_value = 0,
    asset_account_id = "",
    depreciation_account_id = "",
    accumulated_depreciation_account_id = "",
    accumulated_depreciation_amount = 0,
  } = fixed_asset || {};

  const defaultAssetAccountId = findCategoryByIdentifier(
    FIXED_ASSET_IDENTIFIER
  );

  const defaultDepreciationAccountId = findCategoryByIdentifier(
    DEPRECIATION_EXPENSE_IDENTIFIER
  );

  const defaultAccumulatedAccountId = findCategoryByIdentifier(
    ACCUMULATED_DEPRECIATION_IDENTIFIER
  );

  return (
    <Formik
      initialValues={{
        asset_name,
        description,
        purchase_price,
        frequency,
        depreciation_start_date: depreciation_start_date
          ? dayjs(depreciation_start_date).format(DISPLAY_DATE)
          : "",
        salvage_amount: salvage_value,
        useful_life,
        method: "Straight line",
        asset_account_id: asset_account_id || defaultAssetAccountId,
        depreciation_account_id:
          depreciation_account_id || defaultDepreciationAccountId,
        accumulated_depreciation_account_id:
          accumulated_depreciation_account_id || defaultAccumulatedAccountId,

        accumulated_depreciation_amount,
      }}
      validateOnChange
      validationSchema={fixedAssetSchema}
      onSubmit={onSubmit}
    >
      {({ submitForm, isSubmitting }) => (
        <Modal.Content size="large" useCustomOverlay>
          <Modal.Header>
            <div>
              <Modal.Title>
                {isEditFlow ? "Edit Fixed Asset" : "Add Fixed Asset"}
              </Modal.Title>
              <Modal.Subtitle>
                <RuleStepper
                  currentStep={currentStep}
                  onStepperClick={setCurrentStep}
                />
              </Modal.Subtitle>
            </div>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body>
            <Form className="t-flex t-flex-col t-gap-6 t-m-0">
              <AssetDetails />
              <Divider />
              <Depreciation />
              <Divider />
              <Categorization />
            </Form>
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Button onClick={close} disabled={isSubmitting} type="button">
              Cancel
            </Button>
            <Button
              customType="primary"
              onClick={submitForm}
              isLoading={isSubmitting}
              disabled={isSubmitting}
            >
              Check schedule
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      )}
    </Formik>
  );
};

export const AddAsset = ({
  close,
  isOpen,
  fixedAssetId,
  depreciationScheduleData,
}: ModalProps & {
  fixedAssetId?: string;
  depreciationScheduleData?: DepreciationSchedulePreview;
}) => {
  const { alertToast, successToast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const [schedulePreview, { data: scheduleData }] =
    useSchedulePreviewMutation();
  const [createFixedAsset] = useCreateFixedAssetMutation();
  const [updateFixedAsset] = useUpdateFixedAssetMutation();

  const onClose = () => {
    setCurrentStep(1);
    close();
  };

  const onAdd = async ({ method, ...values }: InitialValues) => {
    try {
      await schedulePreview({
        entityId,
        groupId,
        method: "STRAIGHT_LINE",
        ...values,
      }).unwrap();
      setCurrentStep(2);
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const onConfirm = async (
    { method, ...values }: InitialValues,
    { resetForm }: FormikHelpers<InitialValues>
  ) => {
    try {
      if (Boolean(fixedAssetId)) {
        await updateFixedAsset({
          entityId,
          groupId,
          fixedAssetId: fixedAssetId!,
          method: "STRAIGHT_LINE",
          ...values,
        }).unwrap();
        resetForm();
        successToast({ message: "Asset updated successfully" });
        onClose();
        return;
      }

      await createFixedAsset({
        entityId,
        groupId,
        method: "STRAIGHT_LINE",
        ...values,
      }).unwrap();
      resetForm();
      successToast({ message: "Fixed asset added" });
      onClose();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose} modal={false}>
      {currentStep === 1 ? (
        <AssetConsole
          onSubmit={onAdd}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          close={close}
          scheduleData={scheduleData ?? depreciationScheduleData}
          isEditFlow={Boolean(fixedAssetId)}
        />
      ) : (
        <Schedule
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          close={close}
          onSubmit={onConfirm}
          scheduleData={scheduleData}
          isEditFlow={Boolean(fixedAssetId)}
        />
      )}
    </Modal.Root>
  );
};
